import { z } from 'zod';
/**
 * Admin-facing DTO for the system-wide infrastructure config (D-019).
 *
 * The list endpoint MASKS secret values — frontend never sees plaintext.
 * `valueMasked` is true when the row has a stored value but the UI
 * intentionally received a placeholder instead. Editing such a row leaves
 * the existing value untouched if the operator submits an empty string.
 */
export const SystemConfigItem = z.object({
    key: z.string(),
    /** Either the actual value (string-typed) or null/placeholder for secrets. */
    value: z.string().nullable(),
    valueMasked: z.boolean(),
    /** Indicates the row currently has a stored value (used for secret UI hints). */
    hasValue: z.boolean(),
    valueType: z.enum(['string', 'number', 'boolean', 'secret', 'enum']),
    /** Populated only when valueType='enum' — the dropdown options. */
    allowedValues: z.array(z.string()).nullable(),
    category: z.string(),
    description: z.string(),
    hotReload: z.boolean(),
    displayOrder: z.number().int(),
    updatedAt: z.string().datetime(),
});
export const ListSystemConfigResponse = z.object({
    items: z.array(SystemConfigItem),
});
export const UpdateSystemConfigRequest = z.object({
    /**
     * New value as a string. Caller is responsible for stringifying numbers /
     * booleans — `valueType` on the row tells the server how to validate.
     * Pass empty string to UNSET a non-secret row (sets value=null).
     * Pass omitted/undefined to leave a secret untouched (no-op for secrets,
     * unset for others).
     */
    value: z.string().nullable().optional(),
});
export const UpdateSystemConfigResponse = z.object({
    item: SystemConfigItem,
});
/**
 * Smoke-test the currently-configured (or operator-overridden) LLM backend
 * for a given cascade layer. Used by the "测试" button on /admin/system-config.
 */
export const TestLlmRequest = z.object({
    layer: z.enum(['l1', 'l2', 'l3']),
    /** Defaults to the saved `llm.lN.backend` when omitted. The UI passes the
     *  preview backend so operators can test before committing the switch. */
    backend: z.string().optional(),
});
export const TestLlmResponse = z.object({
    ok: z.boolean(),
    layer: z.enum(['l1', 'l2', 'l3']),
    backend: z.string(),
    model: z.string().nullable(),
    url: z.string().nullable(),
    durationMs: z.number().int(),
    /** L1 only: vector dimension actually returned by the embedding API. */
    vectorDim: z.number().int().nullable(),
    /** L1 only: first 8 floats of the embedding (for sanity-check display). */
    vectorPreview: z.array(z.number()).nullable(),
    /** L2/L3 only: the prompt that was sent. */
    prompt: z.string().nullable(),
    /** L2/L3 only: raw text returned by the model (pre-parse). */
    rawResponse: z.string().nullable(),
    /** L2/L3 only: parsed LeadJudgment, if JSON parsing succeeded. */
    parsedJudgment: z
        .object({
        isLead: z.boolean(),
        category: z.string().nullable(),
        tags: z.array(z.string()),
        intent: z.string().nullable(),
        confidence: z.number(),
        suggestedOpener: z.string().nullable(),
        rationale: z.string().nullable(),
    })
        .nullable(),
    inputTokens: z.number().int().nullable(),
    outputTokens: z.number().int().nullable(),
    costUsd: z.number().nullable(),
    /** Populated when ok=false. Includes both the message and a sanitized stack tail. */
    error: z.string().nullable(),
});
//# sourceMappingURL=system-config.js.map